import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api, { apiDownloadUrl } from '../api';
import ExcelImportPanel from '../components/ExcelImportPanel';
import { pickContractorTeamId, normTeamRole } from '../pickContractorTeam';
import { getAuth, isCompanyAdmin, setAuth, getToken } from '../authStorage';

const LAST_PROJECT_KEY = 'ganttLastProjectId';
const LAST_TEAM_KEY = 'ganttLastTeamId';

function readStoredProgressProjectId() {
  try {
    return sessionStorage.getItem(LAST_PROJECT_KEY) || '';
  } catch {
    return '';
  }
}

function readStoredProgressTeamId() {
  try {
    return sessionStorage.getItem(LAST_TEAM_KEY) || '';
  } catch {
    return '';
  }
}

/** Giống Gantt: chuỗi rỗng = xem mọi đội trên dự án (quản trị, ≥2 đội). Legacy ?team=__all__ vẫn chỉ dẫn. */
function normalizeTeamQueryParam(raw) {
  const s = raw == null ? '' : String(raw).trim();
  if (s === '__all__') return '';
  return s;
}

/** Quản trị đang lọc “cả dự án” (có cột Đội). */
function progressAdminAllTeamsMode(teamId, teamsLen) {
  return isCompanyAdmin() && teamsLen > 1 && !teamId;
}

const ProgressPage = () => {
  const { search } = useLocation();
  const sp = useMemo(() => new URLSearchParams(search), [search]);
  const projectFromUrl = sp.get('project') || '';
  const teamParamRaw = sp.get('team');
  const teamFromUrlPresent = teamParamRaw !== null;

  const [projects, setProjects] = useState([]);
  const [teams, setTeams] = useState([]);
  const [projectId, setProjectId] = useState(() => projectFromUrl || readStoredProgressProjectId());
  const [teamId, setTeamId] = useState(() =>
    teamFromUrlPresent ? normalizeTeamQueryParam(teamParamRaw ?? '') : ''
  );
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [logsOpen, setLogsOpen] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(null);
  const importPanelRef = useRef(null);
  const [authRefresh, setAuthRefresh] = useState(0);

  const blueImportButtonStyle = {
    padding: '10px 18px',
    background: 'linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontWeight: 600,
    fontSize: 14,
    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
    cursor: 'pointer',
    font: 'inherit',
  };

  const roleLookupId = useMemo(() => {
    if (isCompanyAdmin()) {
      if (progressAdminAllTeamsMode(teamId, teams.length)) return '';
      if (teamId) return String(teamId);
      if (teams.length === 1) return String(teams[0].id);
      return '';
    }
    return pickContractorTeamId(teams, getAuth()?.teams, projectId, getAuth()?.user?.id);
  }, [teamId, teams, projectId, authRefresh]);

  /** Import Excel: khi "Tất cả đội", mặc định đội đầu danh sách (có thể đổi trong form import). */
  const importDefaultTeamId = useMemo(() => {
    if (!isCompanyAdmin()) return roleLookupId;
    if (progressAdminAllTeamsMode(teamId, teams.length)) return teams[0]?.id != null ? String(teams[0].id) : '';
    return roleLookupId;
  }, [teamId, teams, roleLookupId]);

  const teamRole = useMemo(() => {
    const auth = getAuth();
    if (!auth?.teams || !roleLookupId) return null;
    const t = auth.teams.find((x) => String(x.id) === String(roleLookupId));
    const raw = t?.team_role ?? null;
    const n = normTeamRole(raw);
    return n || null;
  }, [roleLookupId, authRefresh]);

  /** viewer không import; null/missing role vẫn cho import (đội cá nhân thường là lead nhưng auth có thể chưa sync). */
  const canImport =
    isCompanyAdmin() ||
    teamRole === 'lead' ||
    teamRole === 'member' ||
    (!isCompanyAdmin() && !!roleLookupId && teamRole !== 'viewer');
  const canLead = isCompanyAdmin() || teamRole === 'lead';

  /** Công việc nhập Excel: đội chỉ sửa %; sửa/xóa chi tiết do quản trị. */
  function canEditImportedDetails(task) {
    const imported = Number(task?.imported_via_excel) === 1;
    if (imported) return isCompanyAdmin();
    return canLead;
  }

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/projects');
        setProjects(res.data || []);
      } catch {
        setProjects([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* ?project= / ?team= trên URL luôn ưu tiên (đồng bộ khi điều hướng bằng link). */
  useEffect(() => {
    const p = sp.get('project') || '';
    if (!p) return;
    setProjectId(p);
    const tr = sp.get('team');
    if (tr !== null) setTeamId(normalizeTeamQueryParam(tr ?? ''));
  }, [search, sp]);

  /* Dự án (từ URL hoặc session) không còn trong danh sách. */
  useEffect(() => {
    if (loading) return;
    if (!projectId) return;
    const ok = projects.some((pr) => String(pr.id) === String(projectId));
    if (!ok) {
      setProjectId('');
      setTeamId('');
    }
  }, [loading, projects, projectId]);

  useEffect(() => {
    try {
      if (!projectId) {
        sessionStorage.removeItem(LAST_PROJECT_KEY);
        sessionStorage.removeItem(LAST_TEAM_KEY);
        return;
      }
      sessionStorage.setItem(LAST_PROJECT_KEY, String(projectId));
      if (isCompanyAdmin()) {
        if (!teamId) sessionStorage.removeItem(LAST_TEAM_KEY);
        else sessionStorage.setItem(LAST_TEAM_KEY, String(teamId));
      } else if (roleLookupId) {
        sessionStorage.setItem(LAST_TEAM_KEY, String(roleLookupId));
      } else {
        sessionStorage.removeItem(LAST_TEAM_KEY);
      }
    } catch {
      /* ignore */
    }
  }, [projectId, teamId, roleLookupId]);

  useEffect(() => {
    if (!projectId) {
      setTeams([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      try {
        const res = await api.get(`/projects/${projectId}/teams`);
        if (!cancelled) setTeams(res.data || []);
      } catch {
        if (!cancelled) setTeams([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    if (!projectId) {
      setTeamId('');
      return undefined;
    }
    if (teams.length === 0) {
      setTeamId('');
      return undefined;
    }

    if (isCompanyAdmin()) {
      const ids = new Set(teams.map((t) => String(t.id)));
      if (teamId === '__all__') {
        setTeamId('');
        return undefined;
      }
      if (teamId && ids.has(String(teamId))) return undefined;
      if (teams.length === 1) {
        setTeamId(String(teams[0].id));
        return undefined;
      }
      if (!teamId || (teamId && !ids.has(String(teamId)))) {
        setTeamId('');
      }
      return undefined;
    }

    const autoId = pickContractorTeamId(teams, getAuth()?.teams, projectId, getAuth()?.user?.id);
    if (autoId && teamId !== autoId) {
      setTeamId(autoId);
    }
    return undefined;
  }, [projectId, teams, teamId, authRefresh]);

  /* Quản trị: nhớ đội đã chọn (cùng key với Gantt), trừ khi URL có ?team=. */
  useEffect(() => {
    if (!isCompanyAdmin()) return;
    if (!projectId || teams.length === 0) return;
    if (teamFromUrlPresent) return;
    const stored = readStoredProgressTeamId();
    if (!stored) return;
    const ids = new Set(teams.map((t) => String(t.id)));
    if (!ids.has(String(stored))) return;
    setTeamId((cur) => (String(cur) === String(stored) ? cur : String(stored)));
  }, [projectId, teams, teamFromUrlPresent]);

  useEffect(() => {
    if (!teamId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/auth/me');
        if (cancelled || !res.data?.user) return;
        const tok = getToken();
        if (tok) setAuth({ token: tok, user: res.data.user, teams: res.data.teams || [] });
        setAuthRefresh((n) => n + 1);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  const loadTasks = useCallback(async () => {
    if (!projectId) {
      setRows([]);
      setDraft({});
      return;
    }
    if (isCompanyAdmin()) {
      if (teams.length === 1 && !teamId) {
        setRows([]);
        setDraft({});
        return;
      }
      const loadProjectWide = !teamId && teams.length !== 1;
      if (loadProjectWide) {
        setMsg('');
        try {
          const res = await api.get(`/projects/${projectId}/tasks`);
          const list = res.data || [];
          setRows(list);
          const d = {};
          list.forEach((t) => {
            d[t.id] = String(Math.round(Number(t.progress) || 0));
          });
          setDraft(d);
        } catch (e) {
          setMsg(e.response?.data?.error || e.message || 'Không tải được công việc');
          setRows([]);
        }
        return;
      }
      if (!teamId) {
        setRows([]);
        setDraft({});
        return;
      }
      setMsg('');
      try {
        const res = await api.get(`/teams/${teamId}/tasks`);
        const list = res.data || [];
        setRows(list);
        const d = {};
        list.forEach((t) => {
          d[t.id] = String(Math.round(Number(t.progress) || 0));
        });
        setDraft(d);
      } catch (e) {
        setMsg(e.response?.data?.error || e.message || 'Không tải được công việc');
        setRows([]);
      }
      return;
    }
    if (!teamId) {
      setRows([]);
      setDraft({});
      return;
    }
    setMsg('');
    try {
      const res = await api.get(`/teams/${teamId}/tasks`);
      const list = res.data || [];
      setRows(list);
      const d = {};
      list.forEach((t) => {
        d[t.id] = String(Math.round(Number(t.progress) || 0));
      });
      setDraft(d);
    } catch (e) {
      setMsg(e.response?.data?.error || e.message || 'Không tải được công việc');
      setRows([]);
    }
  }, [teamId, projectId, teams]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  async function saveRow(taskId) {
    let v = Number(draft[taskId]);
    if (Number.isNaN(v)) v = 0;
    v = Math.min(100, Math.max(0, v));
    setSavingId(taskId);
    setMsg('');
    try {
      await api.patch(`/tasks/${taskId}/progress`, { progress: v });
      setDraft((prev) => ({ ...prev, [taskId]: String(v) }));
      setMsg('Đã lưu tiến độ.');
    } catch (e) {
      const code = e.code;
      const netHint =
        code === 'ERR_NETWORK' || e.message === 'Network Error'
          ? ' Không kết nối được API — bật backend (cổng khớp PROXY_API_TARGET / PORT), chạy lại npm start sau khi sửa setupProxy; nếu mở app bằng *.test (Laragon) và gọi thẳng API: thêm origin vào backend CORS_ALLOWED_ORIGINS.'
          : '';
      setMsg((e.response?.data?.error || e.message || 'Lỗi lưu') + netHint);
    } finally {
      setSavingId(null);
    }
  }

  async function openLogs(taskId) {
    setLogsOpen(taskId);
    setLogs([]);
    setLogsLoading(true);
    try {
      const res = await api.get(`/tasks/${taskId}/progress-logs`);
      setLogs(res.data || []);
    } catch {
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }

  async function deleteTask(taskId) {
    if (!window.confirm('Xóa công việc này và toàn bộ nhánh con?')) return;
    setMsg('');
    try {
      await api.delete(`/tasks/${taskId}`);
      setMsg('Đã xóa.');
      await loadTasks();
    } catch (e) {
      setMsg(e.response?.data?.error || e.message || 'Không xóa được');
    }
  }

  const activeTeamName = useMemo(() => {
    if (!roleLookupId || !teams.length) return '';
    const t = teams.find((x) => String(x.id) === String(roleLookupId));
    return t?.name || '';
  }, [teams, roleLookupId]);

  const authUsername = useMemo(() => getAuth()?.user?.username || '', []);

  const showContractorEmptyHint =
    !isCompanyAdmin() &&
    projectId &&
    (teams.length === 0 || (roleLookupId && rows.length === 0)) &&
    !(msg && String(msg).includes('Không'));

  if (loading) return <p>Đang tải…</p>;

  const templateUrl = apiDownloadUrl('sample-tasks-template');

  return (
    <div>
      <h2 style={{ marginTop: 0, marginBottom: 12, fontSize: '1.75rem' }}>Tiến độ thi công</h2>
      <p style={{ fontSize: 14, color: '#334155', marginBottom: 10, lineHeight: 1.5 }}>
        Chọn dự án và import file excel công việc theo mẫu (
        <a href={templateUrl} download style={{ color: '#2563eb', fontWeight: 600 }}>
          tải mẫu
        </a>
        ).
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 12 }}>
        <select
          value={projectId}
          aria-label="Dự án"
          onChange={(ev) => {
            const v = ev.target.value;
            setProjectId(v);
            setTeamId('');
            setRows([]);
            try {
              sessionStorage.removeItem(LAST_TEAM_KEY);
            } catch {
              /* ignore */
            }
          }}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', minWidth: 220 }}
        >
          <option value="">— Chọn dự án —</option>
          {projects.map((p) => (
            <option key={p.id} value={String(p.id)}>
              {p.name}
            </option>
          ))}
        </select>
        {isCompanyAdmin() ? (
          <select
            value={teamId}
            aria-label="Đội thi công"
            onChange={(ev) => setTeamId(ev.target.value)}
            disabled={!projectId}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', minWidth: 240 }}
          >
            <option value="">— Tất cả đội (cả dự án) —</option>
            {teams.map((t) => (
              <option key={t.id} value={String(t.id)}>
                {t.name}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      <div style={{ marginBottom: 16 }}>
        {projectId &&
        canImport &&
        (isCompanyAdmin()
          ? progressAdminAllTeamsMode(teamId, teams.length)
            ? teams.length > 0
            : !!roleLookupId
          : !!roleLookupId) ? (
          <button
            type="button"
            onClick={() => importPanelRef.current?.openPicker()}
            style={blueImportButtonStyle}
          >
            Import file Excel (công việc)
          </button>
        ) : (
          <span
            title={
              !projectId
                ? 'Chọn dự án trước'
                : !canImport
                  ? 'Chỉ được xem — không import được.'
                  : isCompanyAdmin() && teams.length === 0
                    ? 'Dự án chưa có đội để import.'
                    : 'Chọn đội để import (hoặc Tất cả đội khi đã có đội trên dự án).'
            }
            style={{
              display: 'inline-block',
              padding: '10px 18px',
              background: '#94a3b8',
              color: '#f1f5f9',
              borderRadius: 10,
              fontWeight: 600,
              fontSize: 14,
              cursor: 'not-allowed',
            }}
          >
            Import file Excel (công việc)
          </span>
        )}
      </div>

      {!isCompanyAdmin() && projectId ? (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 12,
            padding: '12px 14px',
            borderRadius: 8,
            border: '2px solid #dc2626',
            background: '#f1f5f9',
            maxWidth: 520,
            marginBottom: 12,
            boxSizing: 'border-box',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                background: '#e2e8f0',
                flexShrink: 0,
              }}
              aria-hidden
            />
            {authUsername ? (
              <div style={{ fontSize: 14, color: '#334155' }}>Tài khoản: {authUsername}</div>
            ) : (
              <div style={{ fontSize: 14, color: '#94a3b8' }}>Tài khoản: …</div>
            )}
          </div>
        </div>
      ) : null}

      {projectId &&
      canImport &&
      (isCompanyAdmin()
        ? progressAdminAllTeamsMode(teamId, teams.length)
          ? teams.length > 0
          : !!roleLookupId
        : !!roleLookupId) ? (
        <ExcelImportPanel
          ref={importPanelRef}
          compact
          defaultProjectId={projectId}
          defaultTeamId={importDefaultTeamId}
          teams={teams}
          onImported={loadTasks}
          readOnlyTeamName={!isCompanyAdmin() && activeTeamName ? activeTeamName : ''}
        />
      ) : null}

      {isCompanyAdmin() && projectId && teams.length === 0 ? (
        <p style={{ marginTop: 4, color: '#b45309', fontSize: 14 }}>Dự án chưa có đội thi công.</p>
      ) : null}

      {showContractorEmptyHint ? (
        <p style={{ marginTop: 4, color: '#b45309', fontSize: 14, maxWidth: 640, marginBottom: 16 }}>
          Chưa có phần việc của bạn trên dự án này. Thử đăng xuất và đăng nhập lại; nếu vẫn trống, liên hệ quản trị.
        </p>
      ) : null}

      {msg ? (
        <p style={{ color: msg.startsWith('Đã') ? '#15803d' : '#b91c1c', marginTop: 4, marginBottom: 8, fontSize: 14 }}>
          {msg}
        </p>
      ) : null}

      {rows.length > 0 && (teamId || progressAdminAllTeamsMode(teamId, teams.length)) ? (
        <table border="1" cellPadding={8} style={{ width: '100%', marginTop: 16, borderCollapse: 'collapse' }}>
          <thead style={{ background: '#f1f5f9' }}>
            <tr>
              <th align="left">Công việc</th>
              {isCompanyAdmin() && progressAdminAllTeamsMode(teamId, teams.length) ? <th>Đội</th> : null}
              <th>Bắt đầu</th>
              <th>Kết thúc</th>
              <th>% hoàn thành</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.id}>
                <td>
                  <strong>{t.name}</strong>
                  {Number(t.imported_via_excel) === 1 ? (
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#0369a1',
                        background: '#e0f2fe',
                        padding: '2px 8px',
                        borderRadius: 6,
                      }}
                      title="Nhập từ Excel — sửa chi tiết cần quản trị"
                    >
                      Excel
                    </span>
                  ) : null}
                  {t.description ? (
                    <div style={{ fontSize: 12, color: '#64748b' }}>{t.description}</div>
                  ) : null}
                </td>
                {isCompanyAdmin() && progressAdminAllTeamsMode(teamId, teams.length) ? (
                  <td style={{ fontSize: 13, color: '#475569', whiteSpace: 'nowrap' }}>
                    {t.team_name || (t.team_id != null ? `#${t.team_id}` : '—')}
                  </td>
                ) : null}
                <td>{t.start_date ? String(t.start_date).slice(0, 10) : '—'}</td>
                <td>{t.end_date ? String(t.end_date).slice(0, 10) : '—'}</td>
                <td style={{ minWidth: 120 }}>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={draft[t.id] ?? ''}
                    disabled={!canImport}
                    onChange={(ev) => setDraft((prev) => ({ ...prev, [t.id]: ev.target.value }))}
                    style={{ width: 72, padding: 6, borderRadius: 6, border: '1px solid #cbd5e1' }}
                  />
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {canImport && (
                    <button
                      type="button"
                      onClick={() => saveRow(t.id)}
                      disabled={savingId === t.id}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 6,
                        border: 'none',
                        background: savingId === t.id ? '#94a3b8' : '#2563eb',
                        color: '#fff',
                        cursor: savingId === t.id ? 'wait' : 'pointer',
                        fontWeight: 600,
                        marginRight: 6,
                      }}
                    >
                      Lưu %
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => openLogs(t.id)}
                    style={{
                      padding: '6px 10px',
                      borderRadius: 6,
                      border: '1px solid #cbd5e1',
                      background: '#fff',
                      marginRight: 6,
                    }}
                  >
                    Nhật ký %
                  </button>
                  {canEditImportedDetails(t) && (
                    <>
                      <button
                        type="button"
                        onClick={() => setEditOpen(t)}
                        style={{
                          padding: '6px 10px',
                          borderRadius: 6,
                          border: '1px solid #cbd5e1',
                          background: '#fff',
                          marginRight: 6,
                        }}
                      >
                        Sửa
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteTask(t.id)}
                        style={{
                          padding: '6px 10px',
                          borderRadius: 6,
                          border: 'none',
                          background: '#dc2626',
                          color: '#fff',
                        }}
                      >
                        Xóa
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {isCompanyAdmin() &&
      rows.length === 0 &&
      !(msg && String(msg).includes('Không')) ? (
        <p style={{ marginTop: 12, color: '#64748b', fontSize: 14 }}>
          {progressAdminAllTeamsMode(teamId, teams.length) || (!teamId && teams.length === 0)
            ? 'Dự án chưa có công việc.'
            : 'Đội chưa có công việc.'}
        </p>
      ) : null}

      {logsOpen != null && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: 16,
          }}
          onClick={() => setLogsOpen(null)}
          onKeyDown={(e) => e.key === 'Escape' && setLogsOpen(null)}
          role="presentation"
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              maxWidth: 560,
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto',
              padding: 20,
              boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <h3 style={{ marginTop: 0 }}>Nhật ký % — công việc #{logsOpen}</h3>
            {logsLoading ? (
              <p>Đang tải…</p>
            ) : logs.length === 0 ? (
              <p style={{ color: '#64748b' }}>Chưa có thay đổi % nào được ghi.</p>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, lineHeight: 1.6 }}>
                {logs.map((l) => (
                  <li key={l.id} style={{ marginBottom: 8 }}>
                    <strong>
                      {l.changed_at ? new Date(l.changed_at).toLocaleString('vi-VN') : ''}
                    </strong>
                    : {l.old_progress}% → <strong>{l.new_progress}%</strong>
                    {l.changed_by_username ? ` — ${l.changed_by_username}` : ''}
                    {l.note ? ` — ${l.note}` : ''}
                  </li>
                ))}
              </ul>
            )}
            <button
              type="button"
              onClick={() => setLogsOpen(null)}
              style={{ marginTop: 16, padding: '8px 16px', borderRadius: 8, border: '1px solid #cbd5e1' }}
            >
              Đóng
            </button>
          </div>
        </div>
      )}

      {editOpen && (
        <TaskEditModal
          task={editOpen}
          tasks={rows}
          onClose={() => setEditOpen(null)}
          onSaved={async () => {
            setEditOpen(null);
            await loadTasks();
            setMsg('Đã cập nhật công việc.');
          }}
        />
      )}
    </div>
  );
};

function TaskEditModal({ task, tasks, onClose, onSaved }) {
  const [name, setName] = useState(task.name || '');
  const [description, setDescription] = useState(task.description || '');
  const [start, setStart] = useState(task.start_date ? String(task.start_date).slice(0, 10) : '');
  const [end, setEnd] = useState(task.end_date ? String(task.end_date).slice(0, 10) : '');
  const [parentId, setParentId] = useState(task.parent_id != null ? String(task.parent_id) : '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      await api.patch(`/tasks/${task.id}`, {
        name,
        description: description || null,
        start_date: start || null,
        end_date: end || null,
        parent_id: parentId === '' ? null : Number(parentId),
      });
      await onSaved();
    } catch (e) {
      setErr(e.response?.data?.error || e.message || 'Lỗi');
    } finally {
      setBusy(false);
    }
  }

  const parentOptions = tasks.filter((t) => t.id !== task.id);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        padding: 16,
      }}
      onClick={onClose}
      role="presentation"
    >
      <form
        style={{
          background: '#fff',
          borderRadius: 12,
          maxWidth: 420,
          width: '100%',
          padding: 20,
          boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
        }}
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ marginTop: 0 }}>Sửa công việc</h3>
        {err && <p style={{ color: '#b91c1c' }}>{err}</p>}
        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={{ fontSize: 13 }}>Tên</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, boxSizing: 'border-box' }}
          />
        </label>
        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={{ fontSize: 13 }}>Mô tả</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, boxSizing: 'border-box' }}
          />
        </label>
        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={{ fontSize: 13 }}>Ngày bắt đầu</span>
          <input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            style={{ display: 'block', marginTop: 4, padding: 8 }}
          />
        </label>
        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={{ fontSize: 13 }}>Ngày kết thúc</span>
          <input
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            style={{ display: 'block', marginTop: 4, padding: 8 }}
          />
        </label>
        <label style={{ display: 'block', marginBottom: 16 }}>
          <span style={{ fontSize: 13 }}>Công việc cha (ID)</span>
          <select
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
            style={{ display: 'block', marginTop: 4, padding: 8, width: '100%' }}
          >
            <option value="">— Không —</option>
            {parentOptions.map((t) => (
              <option key={t.id} value={String(t.id)}>
                #{t.id} {t.name}
              </option>
            ))}
          </select>
        </label>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="submit"
            disabled={busy}
            style={{
              padding: '10px 18px',
              border: 'none',
              borderRadius: 8,
              background: busy ? '#94a3b8' : '#059669',
              color: '#fff',
              fontWeight: 600,
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            {busy ? 'Đang lưu…' : 'Lưu'}
          </button>
          <button type="button" onClick={onClose} style={{ padding: '10px 18px', borderRadius: 8, border: '1px solid #cbd5e1' }}>
            Hủy
          </button>
        </div>
      </form>
    </div>
  );
}

export default ProgressPage;
